import React from 'react'
import { Link } from 'react-router-dom'
import {
  Building2,
  Database,
  Users,
  TrendingUp,
  Activity,
  Plus,
  ArrowRight,
  Loader2,
  MapPin
} from 'lucide-react'
import { useQuery } from 'react-query'
import { useAuth } from '@/context/AuthContext'
import { brandsService } from '@/services/brands'
import { dataInputsService } from '@/services/dataInputs'
import { usersService } from '@/services/users'
import { auditService } from '@/services/audit'
import { dashboardService } from '@/services/dashboard'
import { ROUTES, USER_ROLES } from '@/utils/constants'

const Dashboard = () => {
  const { user, hasRole } = useAuth()

  // Fetch dashboard statistics
  const { data: brandsData, isLoading: brandsLoading } = useQuery(
    'dashboard-brands',
    () => brandsService.getAll({ page_size: 1000 }),
    { staleTime: 5 * 60 * 1000 } // 5 minutes
  )

  const { data: dataInputsData, isLoading: dataInputsLoading } = useQuery(
    'dashboard-data-inputs',
    () => dataInputsService.getAll({ size: 1000 }),
    { staleTime: 5 * 60 * 1000 }
  )

  const { data: usersData, isLoading: usersLoading } = useQuery(
    'dashboard-users',
    () => usersService.getAll({ size: 1000 }),
    {
      enabled: hasRole(USER_ROLES.ADMIN),
      staleTime: 5 * 60 * 1000
    }
  )

  const { data: recordsTodayData, isLoading: recordsTodayLoading } = useQuery(
    ['dashboard-records-today', new Date().toDateString()], // Use date string for daily refresh
    () => dashboardService.getRecordsToday(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: true,
      onError: (error) => {
        console.error('Error loading records today:', error)
      }
    }
  )

  // Fetch recent activities based on user role
  const { data: recentActivitiesData, isLoading: activitiesLoading } = useQuery(
    'dashboard-recent-activities',
    () => auditService.getRecentActivities(4, user?.role, user?.id),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      onError: (error) => {
        console.error('Error loading recent activities:', error)
      }
    }
  )

  // Calculate statistics
  const stats = [
    {
      name: 'Total Brands',
      value: brandsLoading ? '...' : (brandsData?.total_count || brandsData?.brands?.length || 0).toString(),
      // change: '+2.1%',
      changeType: 'increase',
      icon: Building2,
      href: `${ROUTES.SETTINGS}?tab=brands`,
      loading: brandsLoading,
    },
    {
      name: 'Data Inputs',
      value: dataInputsLoading ? '...' : (dataInputsData?.total_count || 0).toString(),
      // change: '+5.4%',
      changeType: 'increase',
      icon: Database,
      href: ROUTES.DATA_INPUTS,
      loading: dataInputsLoading,
    },
    {
      name: 'Active Users',
      value: usersLoading ? '...' : ((usersData?.users || usersData || []).filter(u => u.is_active)?.length || 0).toString(),
      // change: '+1.2%',
      changeType: 'increase',
      icon: Users,
      href: `${ROUTES.SETTINGS}?tab=users`,
      adminOrManagerOnly: true,
      loading: usersLoading,
    },
    {
      name: 'Records Today',
      value: recordsTodayLoading ? '...' : (recordsTodayData?.total_records || 0).toString(),
      // change: '+12.5%',
      changeType: 'increase',
      icon: Activity,
      href: ROUTES.DATA_INPUTS,
      loading: recordsTodayLoading,
    },
  ]

  // Helper function to format time ago
  const formatTimeAgo = (dateString) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInSeconds = Math.floor((now - date) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  // Helper function to format audit logs for recent activity display
  const formatRecentActivity = (logs) => {
    if (!logs || !logs.logs) return []

    return logs.logs.map(log => ({
      id: log.id,
      type: log.action,
      message: log.operation_description,
      user: log.user_email || 'System',
      time: formatTimeAgo(log.created_at),
      success: log.success
    }))
  }

  const recentActivity = formatRecentActivity(recentActivitiesData)

  const quickActions = [
    {
      name: 'Create Brand',
      description: 'Add a new brand to the system',
      href: ROUTES.BRAND_CREATE,
      icon: Building2,
      color: 'bg-blue-500',
    },
    {
      name: 'Create Area',
      description: 'Set up a new organizational area',
      href: ROUTES.AREA_CREATE,
      icon: MapPin,
      color: 'bg-teal-500',
      adminOrManagerOnly: true,
    },
    {
      name: 'Create Data Input',
      description: 'Set up a new data collection form',
      href: ROUTES.DATA_INPUT_CREATE,
      icon: Database,
      color: 'bg-green-500',
    },
    {
      name: 'Add User',
      description: 'Invite a new user to the platform',
      href: ROUTES.USER_CREATE,
      icon: Users,
      color: 'bg-purple-500',
      adminOrManagerOnly: true,
    },
  ]

  const filteredStats = stats.filter(stat =>
    (!stat.adminOnly || hasRole(USER_ROLES.ADMIN)) &&
    (!stat.adminOrManagerOnly || hasRole(USER_ROLES.ADMIN) || hasRole(USER_ROLES.MANAGER))
  )
  const filteredActions = quickActions.filter(action =>
    (!action.adminOnly || hasRole(USER_ROLES.ADMIN)) &&
    (!action.adminOrManagerOnly || hasRole(USER_ROLES.ADMIN) || hasRole(USER_ROLES.MANAGER))
  )

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.full_name || user?.email}!
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Here's what's happening with your data today.
            </p>
          </div>
          <div className="hidden sm:block">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Activity className="h-4 w-4" />
              <span>Last login: Today at 9:30 AM</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {filteredStats.map((stat) => {
          const Icon = stat.icon
          return (
            <Link
              key={stat.name}
              to={stat.href}
              className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Icon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900 flex items-center">
                          {stat.loading ? (
                            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                          ) : (
                            stat.value
                          )}
                        </div>
                        {!stat.loading && (
                          <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                            stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {stat.change}
                          </div>
                        )}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
            <p className="mt-1 text-sm text-gray-600">
              Common tasks to get you started
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4">
              {filteredActions.map((action) => {
                const Icon = action.icon
                return (
                  <Link
                    key={action.name}
                    to={action.href}
                    className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className={`flex-shrink-0 h-10 w-10 rounded-lg ${action.color} flex items-center justify-center`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {action.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {action.description}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  </Link>
                )
              })}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
            <p className="mt-1 text-sm text-gray-600">
              {(hasRole('admin') || hasRole('manager'))
                ? 'Latest updates from your team'
                : 'Latest update you made'
              }
            </p>
          </div>
          <div className="p-6">
            {activitiesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">Loading activities...</span>
              </div>
            ) : recentActivity.length > 0 ? (
              <>
                <div className="flow-root">
                  <ul className="-mb-8">
                    {recentActivity.map((activity, activityIdx) => (
                      <li key={activity.id}>
                        <div className="relative pb-8">
                          {activityIdx !== recentActivity.length - 1 ? (
                            <span
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          ) : null}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                                activity.success ? 'bg-green-100' : 'bg-red-100'
                              }`}>
                                <Activity className={`h-4 w-4 ${
                                  activity.success ? 'text-green-600' : 'text-red-600'
                                }`} />
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                              <div>
                                <p className="text-sm text-gray-900">
                                  {activity.message}
                                </p>
                                <p className="text-xs text-gray-500">
                                  by {activity.user}
                                </p>
                              </div>
                              <div className="text-right text-xs text-gray-500 whitespace-nowrap">
                                {activity.time}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Only show "View all activity" link for Admin and Manager roles */}
                {(hasRole(USER_ROLES.ADMIN) || hasRole(USER_ROLES.MANAGER)) && (
                  <div className="mt-6">
                    <Link
                      to="/audit-logs"
                      className="text-sm font-medium text-primary-600 hover:text-primary-500"
                    >
                      View all activity →
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <Activity className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Activity will appear here as you use the system.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
